import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useTranslation } from "../../i18n";

/**
 * Preset cron expressions with human-readable labels.
 */
const PRESETS = [
  { labelKey: "cron.preset.disabled", value: "" },
  { labelKey: "cron.preset.daily10am", value: "0 10 * * *" },
  { labelKey: "cron.preset.daily9am", value: "0 9 * * *" },
  { labelKey: "cron.preset.weeklyMonday", value: "0 10 * * 1" },
  { labelKey: "cron.preset.monthly1st", value: "0 10 1 * *" },
];

const CRON_PARTS = [
  { key: "minute", labelKey: "cron.minute", min: 0, max: 59 },
  { key: "hour", labelKey: "cron.hour", min: 0, max: 23 },
  { key: "day", labelKey: "cron.day", min: 1, max: 31 },
  { key: "month", labelKey: "cron.month", min: 1, max: 12 },
  { key: "weekday", labelKey: "cron.weekday", min: 0, max: 6 },
];

/**
 * CronPicker — user-friendly cron schedule selector.
 *
 * Shows preset dropdown + manual expression input with preview.
 * Value is always a 5-field cron string (or empty for "manual only").
 *
 * Each field keeps whatever cron expression the operator typed — a list
 * (`2,4`) or a range (`1-5`) as well as a single number — so a Tuesday and
 * Thursday schedule is expressible. It used to run `parseInt` on blur, which
 * turned `2,4` into `2` and silently dropped the second day.
 */
export default function CronPicker({ value = "", onChange, disabled = false }) {
  const { t } = useTranslation();
  const [customMode, setCustomMode] = useState(
    !isPreset(value) && value !== "",
  );
  const [draft, setDraft] = useState(() => parseParts(value));
  const draftRef = useRef(draft);
  const syncingRef = useRef(false);

  const preview = describeCron(value, t);

  const setPreset = (presetValue) => {
    setCustomMode(false);
    onChange(presetValue);
  };

  const setCustom = (cronStr) => {
    onChange(cronStr);
  };

  const commitDraft = useCallback(
    (partsArr) => {
      const cron = partsArr.join(" ");
      if (isValidCron(cron)) {
        syncingRef.current = true;
        onChange(cron);
      }
    },
    [onChange],
  );

  // Sync draft when value changes externally (raw input, preset, etc.)
  useEffect(() => {
    if (syncingRef.current) {
      syncingRef.current = false;
      return;
    }
    const synced = parseParts(value);
    draftRef.current = synced;
    setDraft(synced);
  }, [value]);

  const enterCustomMode = () => {
    const synced = parseParts(value);
    draftRef.current = synced;
    setDraft(synced);
    setCustomMode(true);
  };

  const updatePart = (index, raw) => {
    const d = [...draftRef.current];
    d[index] = raw;
    draftRef.current = d;
    setDraft(d);
  };

  const commitPart = (index) => {
    const d = [...draftRef.current];
    const raw = String(d[index] ?? "").trim();
    const { min, max } = CRON_PARTS[index];
    if (raw === "" || raw === "*") {
      d[index] = "*";
    } else if (isValidField(raw, min, max)) {
      // Keep the whole expression: a list or a range is a valid cron field,
      // and collapsing it to its first number lost the rest of the schedule.
      d[index] = raw;
    } else {
      d[index] = "*";
    }
    draftRef.current = d;
    setDraft(d);
    commitDraft(d);
  };

  return (
    <div className="space-y-2">
      <Label>{t("cron.label")}</Label>

      {/* Preset dropdown */}
      <div className="flex gap-2">
        <div className="flex-1">
          <select
            value={customMode ? "__custom__" : value}
            onChange={(e) => {
              if (e.target.value === "__custom__") {
                enterCustomMode();
              } else {
                setPreset(e.target.value);
              }
            }}
            disabled={disabled}
            className="flex h-10 w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0"
          >
            {PRESETS.map((p) => (
              <option key={p.value || "__empty__"} value={p.value}>
                {t(p.labelKey)}
              </option>
            ))}
            {!isPreset(value) && value !== "" && (
              <option value={value}>{value}</option>
            )}
            <option value="__custom__">{t("cron.preset.custom")}</option>
          </select>
        </div>
      </div>

      {/* Manual mode: part-by-part inputs */}
      {customMode && (
        <div className="space-y-2">
          <div className="grid grid-cols-5 gap-1">
            {CRON_PARTS.map((part, i) => (
              <div key={part.key} className="flex flex-col items-center">
                <Label className="text-xs text-c-gray-400 mb-1">
                  {t(part.labelKey)}
                </Label>
                <Input
                  type="text"
                  value={draft[i] === "*" ? "" : draft[i]}
                  placeholder="*"
                  disabled={disabled}
                  onChange={(e) => updatePart(i, e.target.value)}
                  onBlur={() => commitPart(i)}
                  className="text-center text-xs px-1 h-8"
                  maxLength={32}
                />
              </div>
            ))}
          </div>
          {/* Raw expression */}
          <Input
            value={value}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="* * * * *"
            disabled={disabled}
            className="font-mono text-xs"
          />
        </div>
      )}

      {/* Preview */}
      {value && (
        <p className="text-xs text-c-gray-400">
          {t("cron.preview")}: {preview}
        </p>
      )}
      {!value && (
        <p className="text-xs text-c-gray-400">{t("cron.noSchedule")}</p>
      )}
    </div>
  );
}

/* ── Helpers ── */

const PRESET_VALUES = new Set(PRESETS.map((p) => p.value));

function isPreset(value) {
  return PRESET_VALUES.has(value);
}

function parseParts(cron) {
  const parts = (cron || "").trim().split(/\s+/);
  while (parts.length < 5) parts.push("*");
  return parts.slice(0, 5);
}

function isValidCron(str) {
  if (!str || str.trim() === "") return true;
  const parts = str.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  return parts.every((part, i) =>
    isValidField(part, CRON_PARTS[i].min, CRON_PARTS[i].max),
  );
}

/**
 * Whether one cron field is a well-formed expression in the given range.
 *
 * Accepts the forms an operator actually needs: `*`, a number, a list
 * (`2,4`), a range (`1-5`), and the step variants (`*`/n, a-b/n). Anything
 * else is a typo and is replaced by `*` on blur rather than silently truncated
 * to its first number.
 */
function isValidField(raw, min, max) {
  const value = String(raw ?? "").trim();
  if (value === "" || value === "*") return true;
  const inRange = (token) => {
    const n = Number(token);
    return Number.isInteger(n) && n >= min && n <= max;
  };
  return value.split(",").every((item) => {
    const [range, step] = item.split("/");
    if (step !== undefined && !inRange(step)) return false;
    if (range === "*") return true;
    const bounds = range.split("-");
    if (bounds.length === 1) return inRange(bounds[0]);
    if (bounds.length === 2) return inRange(bounds[0]) && inRange(bounds[1]);
    return false;
  });
}

/**
 * Human-readable description of a cron expression.
 */
function describeCron(cron, t) {
  if (!cron || cron.trim() === "") return t("cron.desc.manualOnly");
  const p = parseParts(cron);
  const [min, hour, dom, mon, dow] = p;

  const weekdays = [
    t("cron.weekdayNames.sun"),
    t("cron.weekdayNames.mon"),
    t("cron.weekdayNames.tue"),
    t("cron.weekdayNames.wed"),
    t("cron.weekdayNames.thu"),
    t("cron.weekdayNames.fri"),
    t("cron.weekdayNames.sat"),
  ];

  const time =
    hour !== "*" && min !== "*"
      ? `${hour}:${min.padStart(2, "0")}`
      : min !== "*"
        ? `:${min.padStart(2, "0")}`
        : "";

  // Every N minutes
  if (
    min.includes("/") &&
    hour === "*" &&
    dom === "*" &&
    mon === "*" &&
    dow === "*"
  ) {
    const step = min.split("/")[1];
    return t("cron.desc.everyNMinutes", { n: step });
  }

  // Every N hours
  if (hour.includes("/") && dom === "*" && mon === "*" && dow === "*") {
    const step = hour.split("/")[1];
    return t("cron.desc.everyNHours", { n: step });
  }

  // Cron treats day-of-month and day-of-week as alternatives: when both are
  // restricted, the job runs on either. Saying so stops "the weekday stopped
  // counting" being read into a preview that simply could not show both.
  if (dom !== "*" && dow !== "*") {
    return t("cron.desc.dayOrWeekday", {
      dom,
      weekdays: describeWeekdays(dow, weekdays),
    });
  }

  // Daily
  if (dom === "*" && mon === "*" && dow === "*" && time) {
    return t("cron.desc.dailyAt", { time });
  }

  // Weekdays: a single day, a list or a range.
  if (dom === "*" && mon === "*" && dow !== "*" && time) {
    const days = describeWeekdays(dow, weekdays);
    if (dow.includes("/") || dow === "*") {
      return t("cron.desc.unsupported", { cron });
    }
    return t("cron.desc.weeklyAt", { day: days, time });
  }

  // Specific day of month (a number, a list or a range).
  if (mon === "*" && dow === "*" && dom !== "*" && time) {
    return t("cron.desc.monthlyAt", { day: dom, time });
  }

  // Nothing matched — never leave the operator with a blank preview.
  return t("cron.desc.unsupported", { cron });
}

/** Weekday field as names: `2,4` → "Вт, Чт", `1-3` → "Пн–Ср". */
function describeWeekdays(dow, names) {
  return String(dow)
    .split(",")
    .map((item) => {
      const bounds = item.split("-");
      if (bounds.length === 2) {
        return `${weekdayName(bounds[0], names)}–${weekdayName(bounds[1], names)}`;
      }
      return weekdayName(item, names);
    })
    .join(", ");
}

function weekdayName(token, names) {
  const index = parseInt(token, 10);
  return Number.isInteger(index) ? names[index] || token : token;
}
